import React from 'react';
import {
  Box,
  Typography,
  Button,
  Container,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  LinearProgress,
  Collapse,
} from '@mui/material';
import {
  Plus as AddIcon,
  ChevronDown as ChevronDownIcon,
  AlertCircle,
} from 'lucide-react';
import { Project, Task } from '../../types';

interface ProjectsViewProps {
  projects: Project[];
  projectFilter: 'all' | 'active' | 'completed';
  setProjectFilter: (filter: 'all' | 'active' | 'completed') => void;
  projectSort: 'name' | 'progress' | 'budget' | 'startDate';
  setProjectSort: (sort: 'name' | 'progress' | 'budget' | 'startDate') => void;
  calculateProgress: (tasks: Task[]) => number;
  setDialogOpen: (open: boolean) => void;
  expandedProjectId: number | null;
  setExpandedProjectId: (id: number | null) => void;
  handleViewProjectDetails: (project: Project) => void;
  handleEditProject: (project: Project) => void;
}

const ProjectsView: React.FC<ProjectsViewProps> = ({
  projects,
  projectFilter,
  setProjectFilter,
  projectSort,
  setProjectSort,
  calculateProgress,
  setDialogOpen,
  expandedProjectId,
  setExpandedProjectId,
  handleViewProjectDetails,
  handleEditProject,
}) => {
  // Filter projects based on selected filter
  const filteredProjects = projectFilter === 'all' 
    ? projects 
    : projectFilter === 'active'
    ? projects.filter(p => p.status === 'Active' || p.status === 'In Progress')
    : projects.filter(p => p.status === 'Completed');

  // Sort projects based on selected sort option
  const sortedProjects = [...filteredProjects].sort((a, b) => {
    if (projectSort === 'name') {
      return a.name.localeCompare(b.name);
    } else if (projectSort === 'progress') {
      return calculateProgress(b.tasks) - calculateProgress(a.tasks);
    } else if (projectSort === 'budget') {
      return (b.spentAmount || 0) - (a.spentAmount || 0);
    } else if (projectSort === 'startDate') {
      return new Date(b.startDate).getTime() - new Date(a.startDate).getTime();
    }
    return 0;
  });

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" sx={{ color: '#2c2c2c', fontWeight: 'bold' }}>
          Projects
        </Typography>
        <Button
          variant="contained"
          sx={{
            backgroundColor: '#f59f1f',
            color: 'white',
            borderRadius: '50%',
            minWidth: '48px',
            width: '48px',
            height: '48px',
            padding: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: 'none',
            '&:hover': { 
              backgroundColor: '#e08f0f'
            }
          }}
          onClick={() => setDialogOpen(true)}
          title="Add New Project"
        >
          <AddIcon />
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        View and manage all projects. Create new projects, track budgets, and monitor progress.
      </Typography>

      {/* Filter Tabs */}
      <Box sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2, 
          justifyContent: 'space-between', 
          alignItems: { xs: 'stretch', md: 'center' } 
        }}>
          <Box sx={{ 
            display: 'flex', 
            gap: { xs: 1, md: 2 },
            overflowX: { xs: 'auto', md: 'visible' },
            '&::-webkit-scrollbar': { display: 'none' },
            msOverflowStyle: 'none',
            scrollbarWidth: 'none'
          }}>
            <Button
              onClick={() => setProjectFilter('all')}
              sx={{
                pb: 1,
                px: { xs: 1.5, md: 2 },
                borderRadius: 0,
                borderBottom: projectFilter === 'all' ? '3px solid #2c2c2c' : '3px solid transparent',
                color: projectFilter === 'all' ? '#2c2c2c' : '#666',
                fontWeight: projectFilter === 'all' ? 'bold' : 'normal',
                backgroundColor: 'transparent',
                fontSize: { xs: '0.75rem', md: '0.875rem' },
                whiteSpace: 'nowrap',
                minWidth: 'auto',
                '&:hover': {
                  backgroundColor: 'rgba(25, 22, 78, 0.05)',
                }
              }}
            >
              All ({projects.length})
            </Button>
            <Button
              onClick={() => setProjectFilter('active')}
              sx={{
                pb: 1,
                px: { xs: 1.5, md: 2 },
                borderRadius: 0,
                borderBottom: projectFilter === 'active' ? '3px solid #2c2c2c' : '3px solid transparent',
                color: projectFilter === 'active' ? '#2c2c2c' : '#666',
                fontWeight: projectFilter === 'active' ? 'bold' : 'normal',
                backgroundColor: 'transparent',
                fontSize: { xs: '0.75rem', md: '0.875rem' },
                whiteSpace: 'nowrap',
                minWidth: 'auto',
                '&:hover': {
                  backgroundColor: 'rgba(25, 22, 78, 0.05)',
                }
              }}
            >
              Active ({projects.filter(p => p.status === 'Active' || p.status === 'In Progress').length})
            </Button>
            <Button
              onClick={() => setProjectFilter('completed')}
              sx={{
                pb: 1,
                px: { xs: 1.5, md: 2 },
                borderRadius: 0,
                borderBottom: projectFilter === 'completed' ? '3px solid #2c2c2c' : '3px solid transparent',
                color: projectFilter === 'completed' ? '#2c2c2c' : '#666',
                fontWeight: projectFilter === 'completed' ? 'bold' : 'normal',
                backgroundColor: 'transparent',
                fontSize: { xs: '0.75rem', md: '0.875rem' },
                whiteSpace: 'nowrap',
                minWidth: 'auto',
                '&:hover': {
                  backgroundColor: 'rgba(25, 22, 78, 0.05)',
                }
              }}
            >
              Completed ({projects.filter(p => p.status === 'Completed').length})
            </Button>
          </Box>
          
          {/* Sort Dropdown */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ 
              color: '#666', 
              fontWeight: 500,
              fontSize: { xs: '0.75rem', md: '0.875rem' }
            }}>
              Sort:
            </Typography>
            <select 
              value={projectSort} 
              onChange={(e) => setProjectSort(e.target.value as 'name' | 'progress' | 'budget' | 'startDate')}
              style={{
                padding: '8px 12px',
                borderRadius: '4px',
                border: '1px solid #e0e0e0',
                backgroundColor: '#fff',
                color: '#2c2c2c',
                fontWeight: '500',
                cursor: 'pointer',
                fontSize: '14px',
                fontFamily: 'inherit',
                width: '100%'
              }}
            >
              <option value="name">Name (A-Z)</option>
              <option value="progress">Progress (High to Low)</option>
              <option value="budget">Spent Amount (High to Low)</option>
              <option value="startDate">Start Date (Newest First)</option>
            </select>
          </Box>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {sortedProjects.map((project) => {
          const isExpanded = expandedProjectId === project.id;
          const toggleExpand = () => {
            setExpandedProjectId(isExpanded ? null : project.id);
          };

          return (
            <Grid size={{ xs: 12, md: 6 }} key={project.id}>
              <Card sx={{ 
                border: '1px solid #e0e0e0', 
                boxShadow: 'none',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: { xs: isExpanded ? 'none' : '0 2px 8px rgba(0,0,0,0.1)', md: 'none' }
                }
              }}>
                <CardContent sx={{ pb: { xs: 1, md: 2 } }}>
                  {/* Header - Always Visible */}
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center', 
                      mb: { xs: 1, md: 2 },
                      cursor: { xs: 'pointer', md: 'default' },
                      transition: 'all 0.2s ease',
                      '&:active': {
                        transform: { xs: 'scale(0.98)', md: 'none' }
                      }
                    }}
                    onClick={() => {
                      if (window.innerWidth < 900) {
                        toggleExpand();
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                      <Typography variant="h6" sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}>
                        {project.name}
                      </Typography>
                      <Box sx={{ 
                        display: { xs: 'flex', md: 'none' }, 
                        ml: 'auto', 
                        mr: 1,
                        transition: 'transform 0.3s ease',
                        transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
                      }}>
                        <ChevronDownIcon size={20} />
                      </Box>
                    </Box>
                    <Chip 
                      label={project.status} 
                      size="small"
                      sx={{
                        backgroundColor: project.status === 'Active' ? '#2c2c2c' : 
                                       project.status === 'Completed' ? '#f59f1f' : '#666666',
                        color: '#ffffff',
                        fontWeight: 'bold',
                        fontSize: { xs: '0.7rem', md: '0.8125rem' }
                      }}
                    />
                  </Box>

                  {/* Quick Info - Always Visible on Mobile */}
                  <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Progress: {calculateProgress(project.tasks)}% • {project.tasks.filter(t => t.completed).length}/{project.tasks.length} tasks
                    </Typography>
                  </Box>

                  {/* Desktop Content - Always Shown */}
                  <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {project.description}
                    </Typography>

                    <Box sx={{ mb: 2, p: 1, backgroundColor: '#f0f4ff', borderRadius: 1, border: '1px solid #e0e8ff' }}>
                      <Typography variant="caption" sx={{ color: '#2c2c2c', fontWeight: '500', display: 'block', mb: 0.5 }}>
                        Started: {new Date(project.startDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                      </Typography>
                      {project.endDate && project.status === 'Completed' && (
                        <Typography variant="caption" sx={{ color: '#2c2c2c', fontWeight: '500' }}>
                          Ended: {new Date(project.endDate as string).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                        </Typography>
                      )}
                    </Box>
                    
                    <Box sx={{ mb: 2, p: 1.5, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#2c2c2c', mb: 1 }}>
                        Budget Overview
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          Budgeted: {project.currency} {project.budgetedAmount?.toLocaleString() || '0'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Spent: {project.currency} {project.spentAmount?.toLocaleString() || '0'}
                        </Typography>
                      </Box>
                      {project.budgetedAmount > 0 && project.spentAmount > project.budgetedAmount && (
                        <Chip 
                          label={`${Math.round(((project.spentAmount - project.budgetedAmount) / project.budgetedAmount) * 100)}% over budget`}
                          size="small"
                          icon={<AlertCircle size={14} />}
                          sx={{
                            backgroundColor: '#ffebee',
                            color: '#c62828',
                            fontSize: '0.7rem',
                            height: '20px',
                            mb: 1,
                            '& .MuiChip-icon': {
                              color: '#c62828'
                            }
                          }}
                        />
                      )}
                      <LinearProgress 
                        variant="determinate" 
                        value={project.budgetedAmount ? Math.min(Math.round((project.spentAmount / project.budgetedAmount) * 100), 100) : 0}
                        sx={{
                          backgroundColor: 'rgba(25, 22, 78, 0.1)',
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: project.spentAmount > project.budgetedAmount ? '#c62828' :
                                           (project.spentAmount / project.budgetedAmount) > 0.9 ? '#f44336' : '#4caf50'
                          }
                        }}
                      />
                      <Typography variant="caption" sx={{ 
                        display: 'block', 
                        mt: 0.5, 
                        color: project.spentAmount > project.budgetedAmount ? '#c62828' : '#666', 
                        textAlign: 'right',
                        fontWeight: project.spentAmount > project.budgetedAmount ? 'bold' : 'normal'
                      }}>
                        {project.budgetedAmount ? Math.round((project.spentAmount / project.budgetedAmount) * 100) : 0}% spent
                      </Typography>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" gutterBottom>
                        Progress: {calculateProgress(project.tasks)}%
                      </Typography>
                      <LinearProgress 
                        variant="determinate" 
                        value={calculateProgress(project.tasks)}
                        sx={{
                          backgroundColor: 'rgba(25, 22, 78, 0.1)',
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: '#f59f1f'
                          }
                        }}
                      />
                    </Box>
                    <Typography variant="body2" gutterBottom>
                      Tasks: {project.tasks.length} ({project.tasks.filter(t => t.completed).length} completed)
                    </Typography>
                  </Box>

                  {/* Mobile Collapsible Content */}
                  <Collapse in={isExpanded} timeout={300} sx={{ display: { xs: 'block', md: 'none' } }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {project.description}
                    </Typography>

                    <Box sx={{ mb: 2, p: 1, backgroundColor: '#f0f4ff', borderRadius: 1, border: '1px solid #e0e8ff' }}>
                      <Typography variant="caption" sx={{ color: '#2c2c2c', fontWeight: '500', display: 'block', mb: 0.5 }}>
                        Started: {new Date(project.startDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                      </Typography>
                      {project.endDate && project.status === 'Completed' && (
                        <Typography variant="caption" sx={{ color: '#2c2c2c', fontWeight: '500' }}>
                          Ended: {new Date(project.endDate as string).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                        </Typography>
                      )}
                    </Box>
                    
                    <Box sx={{ mb: 2, p: 1.5, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#2c2c2c', mb: 1 }}>
                        Budget Overview
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          Budgeted: {project.currency} {project.budgetedAmount?.toLocaleString() || '0'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Spent: {project.currency} {project.spentAmount?.toLocaleString() || '0'}
                        </Typography>
                      </Box>
                      <LinearProgress 
                        variant="determinate" 
                        value={project.budgetedAmount ? Math.min(Math.round((project.spentAmount / project.budgetedAmount) * 100), 100) : 0}
                        sx={{
                          backgroundColor: 'rgba(25, 22, 78, 0.1)',
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: project.spentAmount > project.budgetedAmount ? '#c62828' : '#4caf50'
                          }
                        }}
                      />
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1, mt: 2, justifyContent: 'flex-start' }}>
                      <Button 
                        size="small" 
                        onClick={() => handleViewProjectDetails(project)}
                        sx={{ color: '#2c2c2c', fontWeight: 'bold', fontSize: '0.75rem' }}
                      >
                        View Details
                      </Button>
                      <Button 
                        size="small" 
                        onClick={() => handleEditProject(project)}
                        sx={{ color: '#f59f1f', fontWeight: 'bold', fontSize: '0.75rem' }}
                      >
                        Edit
                      </Button>
                    </Box>
                  </Collapse>
                </CardContent>
                
                {/* Desktop Action Buttons */}
                <CardActions sx={{ display: { xs: 'none', md: 'flex' } }}>
                  <Button 
                    size="small" 
                    onClick={() => handleViewProjectDetails(project)}
                    sx={{ color: '#2c2c2c', fontWeight: 'bold' }}
                  >
                    View Details
                  </Button>
                  <Button 
                    size="small" 
                    onClick={() => handleEditProject(project)}
                    sx={{ color: '#f59f1f', fontWeight: 'bold' }}
                  >
                    Edit
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Container>
  );
};

export default ProjectsView;
